const isSuccess = response =>
  response && response.status >= 200 && response.status < 300;

const getError = response => {
  let errors = [{ message: 'An unknown error occurred' }];
  if (response?.status >= 500) {
    errors = [
      {
        errorCode: 'SERVER_ERROR',
        message:
          "We're unable to complete your request right now. Please try again later or contact support if the issue persists.",
      },
    ];
  } else if (response?.data?.errors && Array.isArray(response?.data?.errors)) {
    errors = response.data.errors;
  } else if (response && response.data) {
    errors = [response.data];
  }
  return { errors, ok: false, data: null, statusCode: response?.status };
};

const getBlobError = async response => {
  const errorText = await response.data.text();
  const errorJson = JSON.parse(errorText);
  return getError({
    data: { errors: [errorJson] },
    status: response.status,
  });
};

const getSuccess = response => ({
  data: response.data,
  errors: [],
  ok: true,
});

const dataProvider = axiosWrapper => ({
  getApiUrl: () => axiosWrapper.defaults.baseURL,
  getMany: async resource => {
    try {
      const response = await axiosWrapper.get(resource);
      return response?.data;
    } catch (error) {
      if (error?.response?.data) {
        return error?.response?.data;
      }
      return { error: [{ message: error.message }] };
    }
  },
  getOne: async (resource, data = null, config = null) => {
    try {
      const response = await axiosWrapper.get(resource, {
        ...(data && { params: data }),
        ...config,
      });
      return response.data;
    } catch (error) {
      if (error?.response?.data) {
        return error?.response?.data;
      }
      return { error: [{ message: error.message }] };
    }
  },
  deleteOne: async (resource, data) => {
    try {
      const response = await axiosWrapper.delete(resource, data);
      return response.data;
    } catch (error) {
      if (error?.response?.data) {
        return error?.response?.data;
      }
      return { error: [{ message: error.message }] };
    }
  },
  createOne: async (resource, data) => {
    try {
      const response = await axiosWrapper.put(resource, data);
      return response.data;
    } catch (error) {
      if (error?.response?.data) {
        return error?.response?.data;
      }
      return { error: [{ message: error.message }] };
    }
  },
  updateOne: async (resource, data) => {
    try {
      const response = await axiosWrapper.post(resource, data);
      return response.data;
    } catch (error) {
      if (error?.response?.data) {
        return error?.response?.data;
      }
      return { error: [{ message: error.message }] };
    }
  },
  postApi: async (resource, data) => {
    try {
      const response = await axiosWrapper.post(resource, data);
      return response.data;
    } catch (error) {
      if (error?.response?.data) {
        return error?.response?.data;
      }
      return { error: [{ message: error.message }] };
    }
  },
  getApi: async (resource, data = null, config = null) => {
    try {
      const response = await axiosWrapper.get(resource, {
        ...(data && { params: data }),
        ...config,
      });
      return response.data;
    } catch (error) {
      if (error?.response?.data) {
        return error?.response?.data;
      }
      return { error: [{ message: error.message }] };
    }
  },
  putApi: async (resource, data) => {
    try {
      const response = await axiosWrapper.put(resource, data);
      return response.data;
    } catch (error) {
      if (error?.response?.data) {
        return error?.response?.data;
      }
      return { error: [{ message: error.message }] };
    }
  },
  get: async (resource, data = null, config = null) => {
    try {
      const response = await axiosWrapper.get(resource, {
        ...(data && { params: data }),
        ...config,
      });
      if (isSuccess(response)) {
        return getSuccess(response);
      }
      return getError(response);
    } catch (error) {
      return getError(error?.response);
    }
  },
  getBlob: async (resource, data = null, config = null) => {
    try {
      const response = await axiosWrapper.get(resource, {
        ...(data && { params: data }),
        ...config,
        responseType: 'blob',
      });
      if (isSuccess(response)) {
        return {
          ...response,
          ok: true,
        };
      }
      return getBlobError(response);
    } catch (error) {
      if (error?.response?.data instanceof Blob) {
        return getBlobError(error.response);
      }
      return getError(error?.response);
    }
  },
  post: async (resource, data) => {
    try {
      const response = await axiosWrapper.post(resource, data);

      if (isSuccess(response)) {
        return getSuccess(response);
      }
      return getError(response);
    } catch (error) {
      return getError(error?.response);
    }
  },
  put: async (resource, data) => {
    try {
      const response = await axiosWrapper.put(resource, data);
      if (isSuccess(response)) {
        return getSuccess(response);
      }
      return getError(response);
    } catch (error) {
      return getError(error?.response);
    }
  },
  patch: async (resource, data) => {
    try {
      const response = await axiosWrapper.patch(resource, data);
      if (isSuccess(response)) {
        return getSuccess(response);
      }
      return getError(response);
    } catch (error) {
      return getError(error?.response);
    }
  },
  remove: async (resource, data) => {
    try {
      const response = await axiosWrapper.delete(resource, data);
      if (isSuccess(response)) {
        return getSuccess(response);
      }
      return getError(response);
    } catch (error) {
      return getError(error?.response);
    }
  },
  readStreamResult: async url => {
    const handlers = axiosWrapper.interceptors.request.handlers || [];
    const config = {
      method: 'get',
      headers: {
        ...axiosWrapper.defaults.headers?.common,
      },
    };
    const resolvedConfig = await handlers.reduce(
      (promise, h) =>
        promise.then(c =>
          typeof h.fulfilled === 'function'
            ? Promise.resolve(h.fulfilled(c))
            : c,
        ),
      Promise.resolve(config),
    );
    const response = await fetch(url, {
      method: 'GET',
      headers: resolvedConfig.headers,
    });

    if (!response.ok) {
      return getError({
        data: { message: response.statusText },
        status: response.status,
      });
    }

    if (!response.body) {
      return getError({
        data: { message: 'No response body stream available' },
        status: response.status,
      });
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    let buffer = '';
    let latestEvent = null;
    let latestData = null;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      // eslint-disable-next-line no-restricted-syntax
      for (const line of lines) {
        const trimmed = line.trim();

        if (!trimmed) {
          // eslint-disable-next-line no-continue
          continue;
        }

        if (trimmed.startsWith('event:')) {
          // eslint-disable-next-line no-unused-vars
          latestEvent = trimmed.slice(6).trim();
        }

        if (trimmed.startsWith('data:')) {
          const raw = trimmed.slice(5).trim();

          try {
            latestData = JSON.parse(raw);
          } catch {
            // ignore bad chunk
          }
        }
      }
    }

    if (!latestData) {
      throw new Error('Stream ended without receiving data payload');
    }

    return getSuccess({ ok: true, data: latestData, errors: [] });
  },
  stream: async (resource, data, { signal } = {}) => {
    const baseURL = axiosWrapper.defaults.baseURL || '';
    const path = resource.startsWith('/') ? resource.slice(1) : resource;
    const url = `${baseURL.replace(/\/$/, '')}/${path}`;
    const config = {
      url: resource,
      method: 'post',
      data,
      headers: {
        ...axiosWrapper.defaults.headers?.common,
        'Content-Type': 'application/json',
      },
    };
    const handlers = axiosWrapper.interceptors.request.handlers || [];
    const resolvedConfig = await handlers.reduce(
      (promise, h) =>
        promise.then(c =>
          typeof h.fulfilled === 'function'
            ? Promise.resolve(h.fulfilled(c))
            : c,
        ),
      Promise.resolve(config),
    );
    return fetch(url, {
      method: 'POST',
      headers: resolvedConfig.headers,
      body: JSON.stringify(data),
      signal,
    });
  },
  custom: () => Promise.resolve({ data: null }),
});

export default dataProvider;
